import isUndefined from 'lodash/isUndefined';
import map from 'lodash/map';
import reject from 'lodash/reject';
import find from 'lodash/find';
import reduce from 'lodash/reduce';
import isNumber from 'lodash/isNumber';
import forEach from 'lodash/forEach';
import head from 'lodash/head';
import last from 'lodash/last';
import split from 'lodash/split';
import get from 'lodash/get';

const UNTAGGED = -1;
const NO_NEXT_STEP = -2;
const OTHER = -3;
const TRIMMED = -4;

export const NODE_TYPES = {
    UNTAGGED,
    NO_NEXT_STEP,
    OTHER,
    TRIMMED,
};

export const REPLAY_STATUS = {
    VALID: 'valid',
    NOT_AVAILABLE: 'notAvailable',
    EXPIRED: 'expired',
    DISABLED_SUB: 'disabledSub',
    DISABLED_APP: 'disabledApp',
    NO_NEXT_STEP: 'noNextStep',
    OTHER: 'other',
    TRACK_EVENT: 'trackEvent',
    UNTAGGED: 'untagged',
    NO_ACCESS: 'noAccess',
};

export const TYPE_TRACKTYPE = 'TrackType';

export function isUntaggedNode (node) {
    const match = head(node.matches);

    return match === UNTAGGED;
}

export function isOtherNode (node) {
    const match = head(node.matches);

    return match === OTHER;
}

export function isTrackEventNode (node, labeler, frozenPathData) {
    return labeler.type(frozenPathData, head(node.matches)) === TYPE_TRACKTYPE;
}

/**
 * @name getPathTree
 * @description
 * Plucks out the row that contains a "children" property (i.e. the result
 * of the treeify aggregation operator).
 * Modifies the rows parameter to help manage memory use for very
 * large paths.
 * @param {Array} rows The raw path data.
 * @return {Object|undefined} The row with a "children" property, or nothing if it isn't found.
 */
export function getPathTree (rows) {
    for (let index = rows.length, row; index >= 0; --index) {
        row = rows[index];

        if (row && row.tree) {
            delete rows[index];

            return getPathTree(row.tree);
        }

        if (row && row.children) {
            delete rows[index];

            return row;
        }
    }
}

/**
 * @name traversePathTree
 * @description
 * A forEach function for a path tree
 * @param {Object} pathRoot The root node of the traversal
 * @param {Function} iterator The iterator function. Takes a single parameter for the current node (which is also the context).
 */
export function traversePathTree (pathRoot, iterator) {
    const stack = [];
    let i;
    let ii;
    stack.push(pathRoot);

    while (stack.length) {
        const node = stack.pop();

        iterator.call(node, node);

        if (node.children) {
            for (i = 0, ii = node.children.length; i < ii; ++i) {
                const child = node.children[i];
                stack.push(child);
            }
        }
    }
}

/**
 * @name nodeHasReplay
 * @description
 * Determines if a node has a replay
 * @param {Object} node Current path node to check
 * @param {Object} labeler The labeler object to determine the type of node
 * @param {Object} frozenVisitors The visitor paths to check for replays
 * @param {Object} frozenPathData The path data to check for node type
 * @param {Array} appIdsWithSessionReplayRoleEnabled The list of app ids that have the session replay role enabled
 * @return {String} Status of whether a valid replay was found or not
 */
export function nodeHasReplay (node, labeler, frozenVisitors, frozenPathData, appIdsWithSessionReplayRoleEnabled) {
    // No next step node
    if (isNoNextStepNode(node)) {
        return REPLAY_STATUS.NO_NEXT_STEP;
    }

    // Other node
    if (isOtherNode(node)) {
        return REPLAY_STATUS.OTHER;
    }

    if (isUntaggedNode(node)) {
        return REPLAY_STATUS.UNTAGGED;
    }

    const firstValidPathKey = Object.keys(frozenVisitors).find((pathKey) => {
        return isVisitorPathInStep(pathKey, node) && frozenVisitors[pathKey].length;
    });

    if (!firstValidPathKey) return REPLAY_STATUS.NOT_AVAILABLE;

    const firstValidPath = frozenVisitors[firstValidPathKey][0];

    // Get the current step in the representative visitor's path
    const depth = node.key?.split(';').length - 1;
    const repVisitorPath = firstValidPath?.path[depth];
    const stepItem = get(repVisitorPath, 'items[0]');
    let app;

    // Replay status checks that can be determined by examining a representative visitor's path
    if (stepItem) {
        app = labeler.getApp(frozenPathData, head(node.matches));

        const currentStepHasReplayEnabled = !!app?.applicationFlags.enableSessionReplay;

        if (!currentStepHasReplayEnabled) {
            return REPLAY_STATUS.DISABLED_APP;
        }

        if (app && !appIdsWithSessionReplayRoleEnabled.includes(app.id)) {
            return REPLAY_STATUS.NO_ACCESS;
        }
    }

    // Status checks that can only be determined by examining every path instead of choosing a representative
    let status = REPLAY_STATUS.NOT_AVAILABLE;
    const someReplaysValid = Object.keys(frozenVisitors).some((pathKey) => {
        // If we have a visitor path with a key that isn't related to the current node, we can skip it
        if (!isVisitorPathInStep(pathKey, node)) {
            return false;
        }

        const visitorPaths = frozenVisitors[pathKey];

        return visitorPaths.some((path) => {
            const currentVisitorPath = path.path[depth];

            const { recordingSessionId, recordingId, time } = currentVisitorPath;

            if (time < app?.recordingStartingEventTime) {
                status = REPLAY_STATUS.EXPIRED;

                return false;
            }

            if (!recordingSessionId || !recordingId) {
                return false;
            }

            // If valid replay data was found, return true.
            return true;
        });
    });

    if (someReplaysValid) {
        status = REPLAY_STATUS.VALID;
    }

    return status;
}

export function processPaths (rootNode) {
    traversePathTree(rootNode, addPlaceholders);
}

/**
 * Modify {node} to include any appropriate "placeholder" nodes, which
 * vary from a single addition to {Node.matches} to a complete {Node} added
 * to {Node.children}.
 *
 * @see {createPlaceholder}
 *
 * @param {Node} node to add placeholders to
 * @return {Node} {node} after modification
 */
export function addPlaceholders (node) {
    if (node.matches && !node.matches.length) {
        node.placeholder = true;

        if (node.trimmed) {
            node.matches.push(TRIMMED);
            node.key += TRIMMED;
        } else {
            node.matches.push(UNTAGGED);
            node.key += UNTAGGED;
        }
    }

    if (isUndefined(node.sum)) {
        node.sum = reduce(node.children || [], (sum, node) => sum + node.count, 0);
    }

    if (node.sum < node.count && node.children) {
        // `node.children` are only missing in tests?
        node.children.push(createNoNextStep(node));
    }

    return node;
}

/**
 * Create a "placeholder" node representing "No Next Step", referring to data
 * from {node}.
 *
 * @param {Node} node to construct data for
 * @property {number} node.key
 * @property {number} node.count
 * @property {number?} node.sum
 * @property {Node[]} children
 * @return {Node}
 */
export function createNoNextStep (node) {
    return {
        key: `${node.key};${NO_NEXT_STEP}`,
        matches: [NO_NEXT_STEP],
        count: node.count - +node.sum,
    };
}

function getLookupKeyForItem (item) {
    if (item.guideId) {
        return item.guideId + item.guideStepId + item.guideEventType;
    }

    return item.pageId || item.featureId || item.trackTypeId || item.agentId;
}

/**
 * @name getVisitorPaths
 * @description
 * Plucks out the rows that represent an individual visitor path.
 * Modifies the rows parameter to help manage memory use for very
 * large paths.
 * @param {Array} rows The raw path data.
 * @param {Array} mapping The mapping of numbers to path/feature IDs
 * @return {Array} Rows that contain a "visitorId" property
 */
export function getVisitorPaths (rows, mapping) {
    const paths = [];
    let row;
    const hashFunction = createIdHashFunctionFromMapping(mapping);
    for (let i = 0, ii = rows.length; i < ii; ++i) {
        row = rows[i];
        if (row && row.visitorId) {
            // Filter rows that are not aggregated
            row.key = getPathKey(hashFunction, map(row.path, 'items'));
            paths.push(row);
            rows[i] = null;
        }
    }

    return paths;
}

function createIdHashFunctionFromMapping (mapping) {
    const reverseLookup = {};
    mapping = reject(mapping, (item) => {
        return !item;
    });
    forEach(mapping, (value, key) => {
        const id = getLookupKeyForItem(value);
        reverseLookup[id] = key;
    });

    return function getIdHash (id) {
        const hash = reverseLookup[id];

        return hash == null ? id : hash;
    };
}

/**
 * @name getPathKey
 * @description
 * Builds a shortened string representation of a path based on the hash of the page/feature IDs.
 * @param {Function} hashFunction Function to hash a page/feature ID
 * @param {Array} path A path in the format [[{"pageId|featureId":"id"},...],...]
 * @param {Number} length How much of the path to use to build the key. Defaults to the length of the path.
 * @return {String} A string in the format '0,1;2;3'. Semicolons separate path steps, commas separate multiple matches for a single step.
 */
function getPathKey (hashFunction, path, length) {
    if (length == null) {
        length = path.length;
    }
    let key = '';
    for (let i = 0; i < length; ++i) {
        const items = path[i];
        if (!items) {
            key += UNTAGGED;
        } else {
            for (let j = 0, jj = items.length; j < jj; ++j) {
                const id = getLookupKeyForItem(items[j]);
                if (id <= 0) {
                    key += id;
                } else if (id) {
                    key += hashFunction(id);
                } else if (isNumber(items[j])) {
                    key += items[j];
                } else {
                    key += UNTAGGED;
                }

                if (j < jj - 1) {
                    key += ',';
                }
            }
        }

        if (i < length - 1) {
            key += ';';
        }
    }

    return key;
}

export function filterByCount (path, root) {
    const minimum = path.definition.minimum || 0;

    if (minimum > 0) {
        const stack = [];
        let i;
        stack.push(root);

        while (stack.length) {
            const node = stack.pop();

            if (node.children) {
                const combinedNode = { count: 0 };

                for (i = 0; i < node.children.length; ++i) {
                    const child = node.children[i];

                    // eslint-disable-next-line max-depth
                    if (child.count > minimum) {
                        stack.push(child);
                    } else {
                        combinedNode.count += child.count;
                        node.children.splice(i, 1);
                        i--;
                    }
                }

                if (combinedNode.count) {
                    const item = NODE_TYPES.NO_NEXT_STEP;

                    combinedNode.matches = [item];
                    combinedNode.key = `${node.key};${item}`;

                    const noNextStep = find(node.children, (child) => {
                        return child.key === combinedNode.key;
                    });
                    // eslint-disable-next-line max-depth
                    if (noNextStep) {
                        noNextStep.count += combinedNode.count;
                    } else {
                        node.children.push(combinedNode);
                    }
                }
            }
        }
    }

    return root;
}

function isNoNextStepNode (step) {
    return last(step.matches) === NO_NEXT_STEP;
}

export function isVisitorPathInStep (visitorPathKey, step) {
    if (step.depth === 0) return true;

    if (isNoNextStepNode(step)) {
        return step.key === `${visitorPathKey};${NO_NEXT_STEP}`;
    }

    const resourcesPerPathStep = split(step.key, ';');
    const resourcesPerVisitorStep = split(visitorPathKey, ';');

    for (let i = 0; i < resourcesPerPathStep.length; i++) {
        if (resourcesPerVisitorStep[i] !== resourcesPerPathStep[i]) return false;
    }

    return true;
}

export function getNextMatchKeys (visitor, step) {
    const stepKey = step.key;
    const visitorKey = visitor.key;
    if (stepKey === visitorKey) return [String(NO_NEXT_STEP)];

    const startOfNextKey = step.key.length + 1;
    const endOfNextKey = visitorKey.indexOf(';', startOfNextKey);

    const key = endOfNextKey === -1 ? visitorKey.slice(startOfNextKey) : visitorKey.slice(startOfNextKey, endOfNextKey);

    return split(key, ',');
}
